/**
 * PlayerDisc — a player's initial on their colour, with an optional ring,
 * live glow and on-turn marker, drawn as ONE SVG.
 *
 * Shared by the top-bar dot strip (PlayerDotsStrip) and the roster chips
 * (PlayerRosterPanel's AvatarChip).
 *
 * Why one SVG: these used to be nested views (glow, ring, dot, triangle) and
 * a native shadow. Android lays out in density-independent pixels and then
 * rounds each view's edges to physical pixels separately. At a 2.625 density a
 * 1 dp ring is 2.6 px, so a ring and a dot rounding differently left the ring
 * 1 px on one side and 3 px on the other. The Android elevation shadow is
 * also lit from above, so the glow always fell heavier below the dot. Here every
 * shape is a circle around the same centre inside one canvas, so it is
 * concentric on every platform no matter where layout rounding lands.
 *
 * Layout: the component occupies exactly the ring's outer diameter, so callers
 * size their rows as before. The glow and marker draw outside that footprint
 * (the canvas is offset by `bleed`) and never affect layout.
 *
 * Team games: `shape` draws the ring and disc in the player's team shape
 * (lib/teamShapes), each a scaled copy around the same centre, so the ring
 * stays even. No shape, or the circle, draws the circles exactly as before.
 * A shaped glow is one blurred halo of the shape (G5 in
 * docs/team-games/turn-glow-options.html), tuned to match the circle's glow.
 */
import React from 'react'
import { PixelRatio, Platform, View } from 'react-native'
import Svg, { Circle, ClipPath, Defs, FeGaussianBlur, Filter, G, Path, Polygon, RadialGradient, Stop } from 'react-native-svg'
import type { TeamShapeName } from '@warcarp/api-types'
import { teamShapeOutset, teamShapePath, teamShapeReach, teamShapeTop } from '../../lib/teamShapes'
import { PlayerInitialGlyph } from './PlayerInitial'

/**
 * Shaped glow (G5 "smooth blur"): one copy of the shape, `spread` × the glow
 * bleed outside the ring (square to its edges, teamShapeOutset), filled with
 * the glow colour and blurred by `blur` × the bleed. Tuned against the
 * circle's radial gradient at the strip size (bleed 5) and the drawer size
 * (bleed 8), measured outward from the ring edge in both themes: the same
 * strength at the ring, the same falloff and the same reach, within a few
 * percent of the glow's peak. The circle's glow spreads into more area as it
 * grows outward, so a pure per-edge fit (0.5) reads a little tight beside it;
 * 0.53 evens that out. The hexagon's flat top and bottom sit closer in, so it
 * spreads a touch further.
 */
export const SHAPED_GLOW: Record<Exclude<TeamShapeName, 'circle'>, { spread: number; blur: number }> = {
  square: { spread: 0.53, blur: 0.3 },
  diamond: { spread: 0.53, blur: 0.3 },
  hexagon: { spread: 0.545, blur: 0.3 },
}

/** A Gaussian blur has no visible tail past three standard deviations. */
const BLUR_TAIL = 3

/**
 * stdDeviation to hand react-native-svg for a blur of `sigma` px. Web and iOS
 * blur by stdDeviation in points (iOS scales by the screen). Android's
 * FeGaussianBlur runs RenderScript in physical pixels with radius
 * 2 × stdDeviation, and RenderScript's sigma is 0.4 × radius + 0.6 px, so
 * convert to get the same blur (radius capped at 25).
 */
function nativeStdDeviation(sigma: number): number {
  if (Platform.OS !== 'android') return sigma
  return Math.min(12.5, Math.max(0, (sigma * PixelRatio.get() - 0.6) / 0.8))
}

export interface PlayerDiscMarker {
  color: string
  width: number
  height: number
  /** Space between the marker's tip and the ring's top edge. */
  gap: number
}

export interface PlayerDiscGeometry {
  /** Ring outer diameter: the component's layout footprint. */
  outer: number
  /** Canvas padding on every side, wide enough for the glow and marker. */
  pad: number
  /** Canvas size (outer + 2 × pad). */
  canvas: number
  /** Centre of every circle in canvas coordinates. */
  centre: number
}

/** Pure geometry so tests can pin the concentric layout without rendering. */
export function playerDiscGeometry({ diameter, ringWidth, ringGap = 0, glowBleed = 0, marker, overshoot = 0 }: {
  diameter: number
  ringWidth: number
  ringGap?: number
  glowBleed?: number
  marker?: PlayerDiscMarker | null
  /** Team games: how far an optical-size shape reaches past the ring's box. */
  overshoot?: number
}): PlayerDiscGeometry {
  const outer = diameter + 2 * (ringGap + ringWidth)
  const pad = Math.max(glowBleed, marker ? marker.gap + marker.height : 0) + overshoot
  const canvas = outer + 2 * pad
  return { outer, pad, canvas, centre: canvas / 2 }
}

/**
 * Radial-gradient stops for the live glow: clear inside the ring, peaking at
 * its edge and fading to nothing at `glowRadius`. Offsets are fractions of
 * the glow radius.
 */
export function glowStops(ringOuterRadius: number, glowRadius: number) {
  const edge = Math.min(0.95, ringOuterRadius / glowRadius)
  // Start the bloom just inside the ring so no background seam shows between
  // the ring and the glow (the ring is opaque and covers the inner part).
  return { clearTo: Math.max(0, edge - 0.04), peak: Math.min(0.99, edge + 0.01) }
}

export function PlayerDisc({
  initial,
  fill,
  diameter,
  fontSize = diameter * 0.42,
  ringWidth,
  ringColor,
  ringGap = 0,
  glowColor,
  glowBleed = 0,
  glowIntensity = 0.75,
  marker,
  shape,
}: {
  initial: string
  fill: string
  /** Diameter of the coloured disc. */
  diameter: number
  fontSize?: number
  ringWidth: number
  ringColor: string
  /** Transparent gap between the disc and the ring. */
  ringGap?: number
  /** Glow colour, or null/undefined for no glow. */
  glowColor?: string | null
  /** How far the glow extends past the ring's outer edge. */
  glowBleed?: number
  glowIntensity?: number
  /** On-turn triangle above the ring, pointing down. */
  marker?: PlayerDiscMarker | null
  /** Team games: the team's shape. Omitted (or 'circle') draws circles. */
  shape?: TeamShapeName
}) {
  const uid = React.useId().replace(/:/g, '')
  const gradId = `disc-glow-${uid}`
  const shaped = shape && shape !== 'circle' ? shape : null
  // Team shapes: the ring and the glow are expanded copies of the dot's own
  // outline, offset square to its edges (teamShapeOutset), so they look as
  // thick on a diamond as on a circle. The optical-size diamond and hexagon
  // reach past the ring's box, and the blurred halo fades out past its own
  // edge, so the canvas grows by however far the ring or the blur's tail pokes
  // out. Circles keep the original drawing exactly.
  const ringSize = shaped ? teamShapeOutset(shaped, diameter, ringGap + ringWidth) : 0
  const halo = shaped && glowColor ? SHAPED_GLOW[shaped] : null
  const haloSize = halo && shaped ? teamShapeOutset(shaped, ringSize, glowBleed * halo.spread) : 0
  const haloBlur = halo ? glowBleed * halo.blur : 0
  // Clip a gapped ring's halo along the middle of the ring band: clipping at
  // its outer edge would anti-alias both edges and leave a dark seam.
  const haloClipSize = shaped ? teamShapeOutset(shaped, diameter, ringGap + ringWidth / 2) : 0
  const plainOuter = diameter + 2 * (ringGap + ringWidth)
  const overshoot = shaped
    ? Math.max(
      0,
      teamShapeReach(shaped, ringSize) - plainOuter / 2,
      halo ? teamShapeReach(shaped, haloSize) + BLUR_TAIL * haloBlur - plainOuter / 2 - glowBleed : 0,
    )
    : 0
  const { outer, pad, canvas, centre } = playerDiscGeometry({ diameter, ringWidth, ringGap, glowBleed, marker, overshoot })
  const glowRadius = outer / 2 + glowBleed
  const stops = glowStops(outer / 2, glowRadius)
  // The marker points at the top of the ring: the hexagon's flat top sits a
  // little lower than the circle's, the diamond's tip a little higher.
  const ringTop = shaped ? centre - teamShapeTop(shaped, ringSize) : pad
  return (
    <View style={{ width: outer, height: outer }} pointerEvents="none">
      <Svg
        width={canvas}
        height={canvas}
        style={{ position: 'absolute', top: -pad, left: -pad }}
        {...(Platform.OS === 'web' ? { 'aria-hidden': true } : { accessible: false })}
      >
        {halo && shaped && glowColor ? (
          // Shaped glow: one blurred halo of the shape. The filter region is
          // the whole canvas, which reaches past the blur's tail, so it is
          // never clipped. A gapped ring (drawer avatars) would show the
          // halo's middle through the gap, so the halo is clipped to outside
          // the ring, as the circle's gradient is clear inside it.
          <>
            <Defs>
              <Filter id={`disc-blur-${uid}`} filterUnits="userSpaceOnUse" x={0} y={0} width={canvas} height={canvas}>
                <FeGaussianBlur stdDeviation={nativeStdDeviation(haloBlur)} />
              </Filter>
              {ringGap > 0 ? (
                <ClipPath id={`disc-clip-${uid}`}>
                  <Path d={`M0 0 H${canvas} V${canvas} H0 Z ${teamShapePath(shaped, centre, centre, haloClipSize)}`} clipRule="evenodd" />
                </ClipPath>
              ) : null}
            </Defs>
            <G clipPath={ringGap > 0 ? `url(#disc-clip-${uid})` : undefined}>
              <Path
                testID="disc-glow"
                d={teamShapePath(shaped, centre, centre, haloSize)}
                fill={glowColor}
                fillOpacity={glowIntensity}
                filter={`url(#disc-blur-${uid})`}
              />
            </G>
          </>
        ) : glowColor ? (
          <>
            <Defs>
              <RadialGradient id={gradId} cx={centre} cy={centre} r={glowRadius} gradientUnits="userSpaceOnUse">
                <Stop offset={0} stopColor={glowColor} stopOpacity={0} />
                <Stop offset={stops.clearTo} stopColor={glowColor} stopOpacity={0} />
                <Stop offset={stops.peak} stopColor={glowColor} stopOpacity={glowIntensity} />
                <Stop offset={1} stopColor={glowColor} stopOpacity={0} />
              </RadialGradient>
            </Defs>
            <Circle cx={centre} cy={centre} r={glowRadius} fill={`url(#${gradId})`} />
          </>
        ) : null}
        {shaped ? (
          <>
            {ringWidth > 0 && ringGap === 0 ? (
              <Path testID="disc-ring" d={teamShapePath(shaped, centre, centre, ringSize)} fill={ringColor} />
            ) : ringWidth > 0 ? (
              <Path testID="disc-ring" d={teamShapePath(shaped, centre, centre, teamShapeOutset(shaped, diameter, ringGap + ringWidth / 2))} fill="none" stroke={ringColor} strokeWidth={ringWidth} />
            ) : null}
            <Path d={teamShapePath(shaped, centre, centre, diameter)} fill={fill} />
          </>
        ) : (
          <>
            {ringWidth > 0 && ringGap === 0 ? (
              // Touching ring: a solid circle under the disc. A stroke whose inner
              // edge meets the disc edge would anti-alias both edges against the
              // background and leave a light hairline between them.
              <Circle cx={centre} cy={centre} r={outer / 2} fill={ringColor} />
            ) : ringWidth > 0 ? (
              <Circle cx={centre} cy={centre} r={outer / 2 - ringWidth / 2} fill="none" stroke={ringColor} strokeWidth={ringWidth} />
            ) : null}
            <Circle cx={centre} cy={centre} r={diameter / 2} fill={fill} />
          </>
        )}
        <PlayerInitialGlyph initial={initial} cx={centre} cy={centre} fontSize={fontSize} />
        {marker ? (
          <Polygon
            points={`${centre - marker.width / 2},${ringTop - marker.gap - marker.height} ${centre + marker.width / 2},${ringTop - marker.gap - marker.height} ${centre},${ringTop - marker.gap}`}
            fill={marker.color}
          />
        ) : null}
      </Svg>
    </View>
  )
}
